import React, {useEffect, useState, useMemo} from "react";
import { BarChart } from "@mui/x-charts";
import { Box, Stack, Divider, Typography, Paper, Chip, FormControl, InputLabel, Select, MenuItem, ButtonGroup, Button } from "@mui/material";
import { PersonRounded, Sort } from "@mui/icons-material";

const PAGE_SIZE = 15; // Reduced for better readability

const TutorsBChart = ({ data }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState('total_student_count');
  const [sortOrder, setSortOrder] = useState('desc');
  const [metricGroup, setMetricGroup] = useState('all'); // 'all', 'students', 'sessions', 'assessments'
  
  // Process and sort data
  const processedData = useMemo(() => {
    if (!data) return [];
    
    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      
      return sortOrder === 'desc' ? bVal - aVal : aVal - aVal;
    });
    
    return sorted;
  }, [data, sortBy, sortOrder]);
  
  // Paginate
  const paginatedData = useMemo(() => {
    const startIdx = currentPage * PAGE_SIZE;
    return processedData.slice(startIdx, startIdx + PAGE_SIZE);
  }, [processedData, currentPage]);

  const totalPages = Math.ceil((processedData?.length || 0) / PAGE_SIZE);

  // Get series based on selected metric group
  const getSeries = () => {
    switch(metricGroup) {
      case 'students':
        return [
          { 
            dataKey: 'total_student_count', 
            label: 'Total Students', 
            color: '#1976d2',
            valueFormatter: (value) => value?.toLocaleString() || '0'
          },
          { 
            dataKey: 'average_student_count', 
            label: 'Avg Students/Session', 
            color: '#2e7d32',
            valueFormatter: (value) => Math.round(value) || '0'
          }
        ];
        
      case 'sessions':
        return [
          { 
            dataKey: 'total_sessions', 
            label: 'Total Sessions', 
            color: '#ff9800',
            valueFormatter: (value) => value?.toLocaleString() || '0'
          }
        ];
        
      case 'assessments':
        return [
          { 
            dataKey: 'assessments_count', 
            label: 'Assessments Completed', 
            color: '#d32f2f',
            valueFormatter: (value) => value?.toLocaleString() || '0'
          }
        ];
        
      case 'all':
      default:
        return [
          { 
            dataKey: 'total_student_count', 
            label: 'Total Students', 
            color: '#1976d2',
            valueFormatter: (value) => value?.toLocaleString() || '0'
          },
          { 
            dataKey: 'total_sessions', 
            label: 'Total Sessions', 
            color: '#ff9800',
            valueFormatter: (value) => value?.toLocaleString() || '0'
          },
          { 
            dataKey: 'assessments_count', 
            label: 'Assessments', 
            color: '#d32f2f',
            valueFormatter: (value) => value?.toLocaleString() || '0'
          }
        ];
    }
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!data || data.length === 0) return null;
    
    const totals = data.reduce((acc, tutor) => ({
      totalStudents: acc.totalStudents + (tutor.total_student_count || 0),
      totalSessions: acc.totalSessions + (tutor.total_sessions || 0),
      totalAssessments: acc.totalAssessments + (tutor.assessments_count || 0),
      avgStudentsPerSession: acc.avgStudentsPerSession + (tutor.average_student_count || 0)
    }), { 
      totalStudents: 0, 
      totalSessions: 0, 
      totalAssessments: 0, 
      avgStudentsPerSession: 0 
    });
    
    return {
      ...totals,
      avgStudentsPerSession: Math.round(totals.avgStudentsPerSession / data.length)
    };
  }, [data]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <Typography variant="h6" fontWeight="bold">
            Tutor Performance
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedData.length} of {data?.length || 0} tutors
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>

      {/* Summary Stats */}
      {summaryStats && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary">Total Students Helped</Typography>
              <Typography variant="h6" color="inherit">{summaryStats.totalStudents.toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Sessions</Typography>
              <Typography variant="h6" color="warning.main">{summaryStats.totalSessions.toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Avg Students/Session</Typography>
              <Typography variant="h6" color="success.main">{summaryStats.avgStudentsPerSession}</Typography>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Controls */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={{ xs: 1.5, sm: 2 }} 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        mb={2}
      >
        {/* View Metrics Select */}
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
          <InputLabel>View Metrics</InputLabel>
          <Select
            value={metricGroup}
            onChange={(e) => setMetricGroup(e.target.value)}
            label="View Metrics"
          >
            <MenuItem value="all">All Key Metrics</MenuItem>
            <MenuItem value="students">Student Metrics</MenuItem>
            <MenuItem value="sessions">Session Metrics</MenuItem>
            <MenuItem value="assessments">Assessment Metrics</MenuItem>
          </Select>
        </FormControl>

        {/* Sort By Select */}
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            label="Sort By"
          >
            <MenuItem value="total_student_count">Total Students</MenuItem>
            <MenuItem value="total_sessions">Total Sessions</MenuItem>
            <MenuItem value="assessments_count">Assessments</MenuItem>
            <MenuItem value="average_student_count">Avg Students/Session</MenuItem>
            <MenuItem value="first_name">Name</MenuItem>
          </Select>
        </FormControl>

        {/* Sort Order Chip */}
        <Chip 
          label={sortOrder === 'desc' ? 'Descending' : 'Ascending'} 
          size="small"
          onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
          icon={<Sort sx={{ transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} />}
          variant="outlined"
          sx={{ 
            alignSelf: { xs: 'flex-start', sm: 'center' },
            width: { xs: 'fit-content', sm: 'auto' }
          }}
        />
      </Stack>

      {/* Chart */}
      <Box sx={{ height: 400 }}>
        <BarChart
          dataset={paginatedData}
          xAxis={[{ 
            scaleType: 'band', 
            dataKey: 'first_name',
            label: 'Tutor Name',
            tickLabelStyle: {
              angle: 0,
              textAnchor: 'end',
              fontSize: 11
            }
          }]}
          yAxis={[{
            label: metricGroup === 'students' ? 'Student Count' : 
                   metricGroup === 'sessions' ? 'Session Count' :
                   metricGroup === 'assessments' ? 'Assessment Count' : 'Count',
            min: 0
          }]}
          series={getSeries()}
          margin={{ top: 70, right: 30, left: 60, bottom: 20 }}
          slotProps={{ 
            legend: paginatedData.length > 0 ? {
              direction: 'row', 
              position: { vertical: 'top', horizontal: 'middle' }, 
              padding: -5 
            } : {
              hidden: true  // Hide legend when no data
            }
          }}
        />
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Page {currentPage + 1} of {totalPages} • {currentPage * PAGE_SIZE + 1}-{Math.min((currentPage + 1) * PAGE_SIZE, data?.length || 0)} of {data?.length || 0}
          </Typography>
          
          <ButtonGroup variant="outlined" size="small">
            <Button 
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
            >
              First
            </Button>
            <Button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
            >
              Previous
            </Button>
            <Button disabled>
              {currentPage + 1}
            </Button>
            <Button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1))}
              disabled={currentPage === totalPages - 1}
            >
              Next
            </Button>
            <Button 
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
            >
              Last
            </Button>
          </ButtonGroup>
        </Box>
      )}
    </Box>
  );
};

export default TutorsBChart;